import React, {useMemo, useState, useContext} from 'react';
import {Button, ButtonGroup, Icon, Image} from '@shopify/polaris';
import {useHistory, Link, useLocation} from 'react-router-dom';
import {UserContext} from '../contexts/userContext';
import useAskAi from '../hooks/useAskAi';
import {apiFetch} from '../hooks/useFetchApi';
import logo from '../resources/images/avada_128.png';
import './MainLayout.css';
import Divider from '../components/Divider/Divider';
import '@shopify/polaris/dist/styles.css';
import {toast} from '../utils/toast';
import {useUserRole} from '../contexts/userRoleContext';
import {getNavigationItems} from '../config/routeConfig';

export default function MainLayout({
  children,
  title = 'Avada Shopify Reviews',
  navItems,
  appOptions = [],
}) {
  const history = useHistory();
  const location = useLocation();
  const {user: userAuth} = useContext(UserContext);

  const isAllow = () => {
    return (
      userAuth?.email?.includes?.('avada.io') ||
      userAuth?.email?.includes?.('avadagroup.com') ||
      userAuth?.email?.includes?.('mageplaza.com') ||
      userAuth?.email?.includes?.('avada.net') ||
      userAuth?.email?.includes?.('avada.net') ||
      userAuth?.email?.includes?.('avada.email') ||
      userAuth?.email?.includes?.('shopplaza.io') ||
      userAuth?.email?.includes?.('epu@gmail.com')
    );
  };

  const userRole = useUserRole();
  const {isAdmin} = userRole;

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [reindexing, setReindexing] = useState(false);

  const {modal: askAiModal, openModal: openAiModal} = useAskAi({appOptions});

  // Get navigation items based on user roles
  const items = useMemo(() => {
    if (navItems) return navItems;

    const navigationItems = getNavigationItems(userRole);
    const userRoutes = navigationItems.user || [];
    const adminRoutes = navigationItems.admin || [];

    return [...userRoutes, ...adminRoutes];
  }, [navItems, userRole]);

  const handleReindex = async () => {
    try {
      setReindexing(true);
      await apiFetch(`${process.env.REACT_APP_API_URL}/api/reindex`, {method: 'POST'});
      toast.success('The data will be updated in the next couple of minutes');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Failed to trigger reindex. Please try again.');
    } finally {
      setReindexing(false);
    }
  };

  const SecondaryActions = () => {
    const actions = [
      <Button key="insights" onClick={openAiModal}>
        AI Review Insights
      </Button>,
      <Button key="neo" onClick={() => history.push('/neo-ai')}>
        Meet Neo.ai
      </Button>,
    ];
    if (isAdmin) {
      actions.unshift(
        <Button key="apps" onClick={() => history.push('/apps')}>
          Apps
        </Button>
      );
    }
    return <ButtonGroup>{actions}</ButtonGroup>;
  };

  return (
    <div style={{display: 'flex', minHeight: '100vh', background: '#F6F6F7'}}>
      {/* Sidebar */}
      <aside
        style={{
          width: sidebarOpen ? 240 : 64,
          transition: 'width 0.2s ease',
          background: '#FFFFFF',
          color: '#000',
          paddingTop: 12,
          paddingBottom: 12,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{padding: '0 12px', marginBottom: 12}}>
          <img
            onClick={() => setSidebarOpen((o) => !o)}
            style={{
              width: 36,
              height: 36,
              borderRadius: 6,
              border: '1px solid #C9CCCF',
              background: 'transparent',
              cursor: 'pointer',
            }}
            aria-label="Toggle navigation"
            src={logo}
          />
        </div>

        <nav style={{display: 'flex', flexDirection: 'column', gap: 6}}>
          {items.map((item) => {
            const isActive = location?.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={isActive ? 'nav-link active' : 'nav-link'}
                style={{
                  color: isActive ? '#2C6ECB' : '#000',
                  textDecoration: 'none',
                  padding: '10px 12px',
                  borderRadius: 8,
                  margin: '0 8px',
                  background: isActive ? 'rgba(0,0,0,0.06)' : 'transparent',
                }}
              >
                <div className="nav-item">
                  {item?.icon &&
                    (sidebarOpen ? (
                      <>
                        <Icon source={item.icon} color={isActive ? 'interactive' : ''} />
                        {item.label}
                      </>
                    ) : (
                      <Icon source={item.icon} color={isActive ? 'interactive' : ''} />
                    ))}

                  {item?.imageSrc &&
                    (sidebarOpen ? (
                      <>
                        <Image
                          width={20}
                          height={20}
                          source={item.imageSrc}
                          color={isActive ? 'interactive' : ''}
                        />
                        {item.label}
                      </>
                    ) : (
                      <Image
                        width={20}
                        height={20}
                        source={item.imageSrc}
                        color={isActive ? 'interactive' : ''}
                      />
                    ))}
                </div>
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main area */}
      <Divider type="vertical" margin={0} width={100} />

      <div style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
        {/* Header */}

        <div
          style={{
            background: '#FFFFFF',
            borderBottom: '1px solid #E1E3E5',
            padding: '14px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div style={{display: 'flex', alignItems: 'center', gap: 12, flex: 1}}>
            <div style={{fontSize: 20, fontWeight: 600, color: '#202223'}}>{title}</div>
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
            <SecondaryActions />
            <Button primary loading={reindexing} onClick={handleReindex}>
              Not updated? Reindex now
            </Button>
          </div>
        </div>

        {/* Content */}
        <div style={{padding: 20}}>
          {isAllow() && children}
          {askAiModal}
        </div>
      </div>

      {null}
    </div>
  );
}
